import { ImageData, ProcessedImageData, BayerOptions } from './esc-pos-image.types';

/**
 * Apply Bayer ordered dithering to an image
 * Uses a Bayer matrix for ordered dithering, creating a regular pattern
 * @param imageData Input image data
 * @param options Bayer options with optional threshold
 * @returns Processed black and white image data with Bayer dithering
 */
export function bayer(imageData: ImageData, options: BayerOptions = {}): ProcessedImageData {
  const { data, width, height } = imageData;
  const threshold = options.threshold || 128;

  // 4x4 Bayer matrix (normalized to 0-255 range)
  // This creates a regular dithering pattern
  const bayerMatrix = [
    [0, 8, 2, 10],
    [12, 4, 14, 6],
    [3, 11, 1, 9],
    [15, 7, 13, 5],
  ];

  // Scale Bayer matrix values to 0-255 range
  const matrixSize = 4;
  const scaleFactor = 255 / 15; // 15 is the max value in the matrix

  const processedData = new Uint8ClampedArray(data.length);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;

      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const alpha = data[idx + 3];

      // Calculate luminance
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      // Get Bayer matrix value for current position
      const matrixX = x % matrixSize;
      const matrixY = y % matrixSize;
      const bayerValue = bayerMatrix[matrixY][matrixX] * scaleFactor;

      // Apply Bayer threshold: compare luminance + bayer offset to threshold
      const adjustedLuminance = luminance + (bayerValue - 128);
      const pixelValue = adjustedLuminance >= threshold ? 255 : 0;

      // Set RGB channels to the same value (black or white)
      processedData[idx] = pixelValue; // Red
      processedData[idx + 1] = pixelValue; // Green
      processedData[idx + 2] = pixelValue; // Blue
      processedData[idx + 3] = alpha; // Keep original alpha
    }
  }

  return {
    data: processedData,
    width,
    height,
  };
}
